import React, { useEffect, useState } from "react";

import { find } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import SettingsModule from "@app/analytics/account/components/SettingsModule";
import type { SettingModule } from "@app/analytics/account/types";
import MultipleCalendarsWarning from "@app/analytics/components/MultipleCalendarsWarning";
import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useSlackIntegration from "@app/analytics/hooks/integrations/useSlackIntegration";
import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import DisconnectCalendarConfirmationModal from "@app/analytics/Integrations/IntegrationDetail/CalendarIntegrationConfig/DisconnectCalendarConfirmationModal";
import {
  INTEGRATION_PROVIDERS,
  INTEGRATION_PROVIDERS_BY_ID,
} from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { Integration } from "@app/common/types";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { isResponseErrorWithStatus } from "@app/common/utils";

import InfoPanel from "@rui/InfoPanel";
import { Link } from "@rui/Link";
import { Column } from "@rui/Stack";

import AdditionalPermissionsRequiredModal from "./AdditionalPermissionsRequiredModal";
import { INTEGRATION_CATEGORIES } from "./constants";
import IntegrationModuleRow from "./IntegrationModuleRow";

const RssCalendarAlert = ({
  hasOtherCalendars,
}: {
  hasOtherCalendars: boolean;
}) => (
  <InfoPanel
    severity="info"
    title="Smart Scheduling Calendar"
    direction="column"
  >
    This calendar is used to schedule meetings with Read Smart Scheduler Links.
    {hasOtherCalendars && (
      <>
        {" "}
        <Link to={ROUTES.USER_SETTINGS_SMART_SCHEDULER} fontWeight={400}>
          Change calendar
        </Link>
        .
      </>
    )}
  </InfoPanel>
);

const WorkspaceConnectedAlert = ({
  providerName,
}: {
  providerName: string;
}) => (
  <InfoPanel
    severity="info"
    direction="column"
    title="Enabled for your Workspace"
  >
    Meeting notes may be posted to {providerName} based on your{" "}
    <Link to={ROUTES.USER_SETTINGS_REPORTING} fontWeight={400}>
      distribution settings
    </Link>
    .
  </InfoPanel>
);

const IntegrationsModule = (props: SettingModule) => {
  const [queryParams] = useSearchParams();

  const [pendingEnabledIntegration, setPendingEnabledIntegration] =
    useState<Integration | null>(null);
  const [
    pendingDisabledCalendarIntegration,
    setPendingDisabledCalendarIntegration,
  ] = useState<Integration | null>(null);

  const {
    userIntegrationsByCategory,
    isProviderConnected,
    isLoading: isIntegrationsLoading,
  } = useIntegrations();
  const { disconnect: disconnectSlack } = useSlackIntegration();

  const { connectedRssCalendarProviders, rssCalendar } = useRssLinksSettings();

  const { deleteConnectedProvider } = useConnectedProviders();
  const {
    authorize: authorizeIntegration,
    disconnect: disconnectIntegration,
    enable: enableIntegration,
  } = useIntegration();

  const action = queryParams.get("action");
  const success = queryParams.get("success");

  useEffect(() => {
    if (!action) return;

    const integration = find({ connectAction: action }, INTEGRATION_PROVIDERS);

    if (!integration) return;

    if (success === "true") {
      toast.success(`${integration.displayName} connected successfully!`);
    } else {
      toast.error(
        `There was an error connecting ${integration.displayName}. Please try again or contact support@read.ai if the problem persists.`
      );
    }
  }, [action, success]);

  const hasMultipleRssSupportedConnectedCalendars =
    connectedRssCalendarProviders.length > 1;

  const handleConfirmDisableCalendarIntegration = () => {
    if (!pendingDisabledCalendarIntegration) return;

    deleteConnectedProvider.mutate(
      pendingDisabledCalendarIntegration.providerId as CalendarProviderId,
      {
        onSuccess: () => setPendingDisabledCalendarIntegration(null),
        onError: () => {
          setPendingDisabledCalendarIntegration(null);
          toast.error(
            `Something went wrong and ${pendingDisabledCalendarIntegration.displayName} could not be disabled. Please try again or contact support@read.ai if the problem persists.`
          );
        },
      }
    );
  };

  const disableIntegration = (integration: Integration) => {
    if (integration.kind === "calendar") {
      return setPendingDisabledCalendarIntegration(integration);
    }

    if (integration.providerId === "slack_bot") {
      // TODO: useIntegration hook should handle disconnecting slack vs. us having to have a special case
      return disconnectSlack.mutate("user", {
        onError: () => {
          toast.error(
            `Something went wrong and ${integration.displayName} could not be disabled. Please try again or contact support@read.ai if the problem persists.`
          );
        },
      });
    }

    disconnectIntegration.mutate(integration.providerId, {
      onError: () => {
        toast.error(
          `Something went wrong and ${integration.displayName} could not be disabled. Please try again or contact support@read.ai if the problem persists.`
        );
      },
    });
  };

  const attemptToEnable = (integration: Integration) => {
    const enableUrl =
      INTEGRATION_PROVIDERS_BY_ID[integration.providerId].enableUrl;
    if (enableUrl) {
      enableIntegration.mutate(enableUrl, {
        onError: (error) => {
          if (isResponseErrorWithStatus(error, 403)) {
            setPendingEnabledIntegration(integration);
          }
        },
      });
    } else {
      setPendingEnabledIntegration(integration);
    }
  };

  const handleToggleIntegration = (
    integration: Integration,
    enable: boolean
  ) => {
    return enable
      ? attemptToEnable(integration)
      : disableIntegration(integration);
  };

  const handleClosePermissionModal = () => setPendingEnabledIntegration(null);

  const handleConfirmConnect = () => {
    // impossible; appease the types
    if (!pendingEnabledIntegration) return null;

    return authorizeIntegration.mutate({
      providerId: pendingEnabledIntegration.providerId,
      scope: "user",
      returnRoute: ROUTES.USER_SETTINGS_INTEGRATIONS,
    });
  };

  return (
    <>
      <MultipleCalendarsWarning kind="warning" />
      <SettingsModule {...props}>
        <Column spacing={1.5} paddingX={1}>
          {INTEGRATION_CATEGORIES.map((category, i) => {
            const integrations = userIntegrationsByCategory[category.category];
            return (
              <React.Fragment key={category.category}>
                {i > 0 && <Divider sx={{ my: 1 }} />}
                <Column spacing="2px">
                  <Typography variant="h3" color="shades.neutral.800">
                    {category.title}
                  </Typography>
                  <Typography
                    variant="bodySecondaryReg"
                    color="shades.neutral.500"
                  >
                    {category.description}
                  </Typography>
                </Column>
                {integrations.map((integration) => {
                  const isRssCalendar =
                    integration.kind === "calendar" &&
                    rssCalendar?.provider === integration.providerId;

                  const isWorkspaceConnected = isProviderConnected({
                    providerId: integration.providerId,
                    scope: "workspace",
                  });

                  const isConfirmingEnable =
                    pendingEnabledIntegration?.providerId ===
                    integration.providerId;

                  const isConfirmingDisable =
                    pendingDisabledCalendarIntegration?.providerId ===
                    integration.providerId;

                  return (
                    <IntegrationModuleRow
                      key={integration.providerId}
                      integration={integration}
                      isLoading={isIntegrationsLoading}
                      isConfirming={isConfirmingEnable || isConfirmingDisable}
                      alert={
                        isRssCalendar ? (
                          <RssCalendarAlert
                            hasOtherCalendars={
                              hasMultipleRssSupportedConnectedCalendars
                            }
                          />
                        ) : isWorkspaceConnected && !integration.isConnected ? (
                          <WorkspaceConnectedAlert
                            providerName={integration.displayNameShort}
                          />
                        ) : null
                      }
                      onToggleEnabled={handleToggleIntegration}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}
        </Column>
      </SettingsModule>

      {pendingEnabledIntegration != null && (
        <AdditionalPermissionsRequiredModal
          providerId={pendingEnabledIntegration.providerId}
          open
          onClose={handleClosePermissionModal}
          onConfirm={handleConfirmConnect}
          loading={authorizeIntegration.isPending}
        />
      )}
      {pendingDisabledCalendarIntegration != null && (
        <DisconnectCalendarConfirmationModal
          calendar={pendingDisabledCalendarIntegration}
          open
          onClose={() => setPendingDisabledCalendarIntegration(null)}
          onConfirm={handleConfirmDisableCalendarIntegration}
          loading={deleteConnectedProvider.isPending}
        />
      )}
    </>
  );
};

export default IntegrationsModule;
